import type { ChartSpec, NotebookKpi, TableSpec } from '../../types';
import { chapterDisplayTitle } from './chapterGate';
import type { DashboardTile, DashboardVisual, NotebookChapterRecord } from './dashboardTypes';
import { defaultPresetForSpec, placeNewTile } from './widgets/widgetLayout';
import type { WidgetSpec } from './widgets/widgetTypes';
import { widgetParamKey } from './widgets/widgetTypes';

export function visualPinId(
  chapterId: string,
  kind: 'chart' | 'table' | 'kpi',
  key: string,
): string {
  return `${chapterId}:${kind}:${key}`;
}

export function chapterPinId(chapterId: string): string {
  return `${chapterId}:chapter`;
}

export function widgetPinId(chapterId: string, paramKey: string): string {
  return `${chapterId}:widget:${paramKey}`;
}

export function chartVisual(
  chapter: NotebookChapterRecord,
  chart: ChartSpec,
  index: number,
): DashboardVisual {
  return {
    id: visualPinId(chapter.id, 'chart', chart.dataRef || String(index)),
    kind: 'chart',
    title: chart.title || 'Chart',
    chart,
  };
}

export function tableVisual(
  chapter: NotebookChapterRecord,
  table: TableSpec,
  index: number,
): DashboardVisual {
  return {
    id: visualPinId(chapter.id, 'table', table.dataRef || String(index)),
    kind: 'table',
    title: table.title || 'Table',
    table,
  };
}

export function kpiVisual(
  chapter: NotebookChapterRecord,
  kpi: NotebookKpi,
): DashboardVisual {
  return {
    id: visualPinId(chapter.id, 'kpi', kpi.field || kpi.label),
    kind: 'kpi',
    title: kpi.label,
    kpi,
  };
}

export function chapterVisuals(chapter: NotebookChapterRecord): DashboardVisual[] {
  const kpis = (chapter.spec.kpis ?? []).map(kpi => kpiVisual(chapter, kpi));
  const charts = (chapter.spec.charts ?? []).map((chart, i) =>
    chartVisual(chapter, chart, i),
  );
  const tables = (chapter.spec.tables ?? []).map((table, i) =>
    tableVisual(chapter, table, i),
  );
  return [...kpis, ...charts, ...tables];
}

export function visualTileFrom(
  chapter: NotebookChapterRecord,
  visual: DashboardVisual,
  existing: DashboardTile[] = [],
): DashboardTile {
  return {
    id: visual.id,
    kind: 'visual',
    title: visual.title,
    sourceChapterId: chapter.id,
    sourceQuestion: chapter.question,
    pinnedAt: new Date().toISOString(),
    visuals: [visual],
    layout: placeNewTile(existing, 'M'),
  };
}

export function widgetTileFrom(
  chapter: NotebookChapterRecord,
  widget: WidgetSpec,
  existing: DashboardTile[] = [],
): DashboardTile {
  const id = widgetPinId(chapter.id, widgetParamKey(widget));
  return {
    id,
    kind: 'widget',
    title: widget.title,
    sourceChapterId: chapter.id,
    sourceQuestion: chapter.question,
    pinnedAt: new Date().toISOString(),
    visuals: [],
    widget,
    layout: placeNewTile(existing, defaultPresetForSpec(widget)),
  };
}

export function chapterTileFrom(chapter: NotebookChapterRecord): DashboardTile {
  return {
    id: chapterPinId(chapter.id),
    kind: 'chapter',
    title: chapterDisplayTitle(chapter.spec, chapter.question),
    sourceChapterId: chapter.id,
    sourceQuestion: chapter.question,
    pinnedAt: new Date().toISOString(),
    visuals: chapterVisuals(chapter),
    layout: { x: 0, y: 0, preset: 'full' },
  };
}

export function refreshItemsFromTiles(tiles: DashboardTile[]) {
  return tiles
    .filter(tile => tile.kind !== 'widget')
    .flatMap(tile =>
      tile.visuals.map(visual => ({
        id: visual.id,
        kind: visual.kind,
        spec:
          visual.kind === 'chart'
            ? visual.chart
            : visual.kind === 'table'
              ? visual.table
              : visual.kpi,
      })),
    );
}

export function tilesNeedLegacyRefresh(tiles: DashboardTile[]): boolean {
  return tiles.some(tile => tile.kind !== 'widget' && tile.visuals.length > 0);
}
